import type { FC } from "react";
import clsx from "clsx";

export interface CsvTableProps {
  rows: string[][];
  className?: string;
}

const TABLE_CLS = clsx(
  "w-full border-collapse text-left text-xs",
  "font-mono text-slate-900 dark:text-slate-200",
);

const HEADER_CELL_CLS = clsx(
  "sticky top-0 z-10 border-b border-slate-200 bg-slate-100 px-2 py-1.5",
  "dark:border-white/10 dark:bg-slate-900",
  "font-sans text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400",
  "whitespace-nowrap",
);

const BODY_CELL_CLS =
  "border-b border-slate-100 px-2 py-1 whitespace-nowrap dark:border-white/5";

/** Lightweight scrollable CSV table for preview surfaces. */
export const CsvTable: FC<CsvTableProps> = ({ rows, className }) => {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="csv-table-empty">
        No rows.
      </p>
    );
  }

  const [header, ...body] = rows;

  return (
    <div className={clsx("min-h-0 overflow-auto", className)} data-testid="csv-table">
      <table className={TABLE_CLS}>
        <thead>
          <tr>
            {header.map((cell, index) => (
              <th key={`header-${index}`} className={HEADER_CELL_CLS}>
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`} className="hover:bg-slate-50 dark:hover:bg-white/5">
              {row.map((cell, cellIndex) => (
                <td key={`cell-${rowIndex}-${cellIndex}`} className={BODY_CELL_CLS}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
